/**
 * resolve-opportunity-url.ts
 *
 * Extracts company name and role hints from a job posting URL.
 * Pure frontend logic — no API calls. Works by parsing known
 * ATS URL patterns and common job board domains.
 */

const ATS_PATTERNS: Array<{
  hostMatcher: RegExp;
  companyExtractor: (host: string, pathname: string) => string | null;
}> = [
  // Lever: jobs.lever.co/{company}
  { hostMatcher: /^jobs\.(?:eu\.)?lever\.co$/, companyExtractor: (_, p) => p.split("/").filter(Boolean)[0] ?? null },
  // Greenhouse: boards.greenhouse.io/{company} or job-boards.greenhouse.io/{company}
  { hostMatcher: /^(?:boards|job-boards(?:\.eu)?)\.greenhouse\.io$/, companyExtractor: (_, p) => p.split("/").filter(Boolean)[0] ?? null },
  // Ashby: jobs.ashbyhq.com/{company}
  { hostMatcher: /^jobs\.ashbyhq\.com$/, companyExtractor: (_, p) => p.split("/").filter(Boolean)[0] ?? null },
  // Workday: {company}.wd{num}.myworkdayjobs.com or {company}.myworkdayjobs.com
  { hostMatcher: /\.myworkdayjobs\.com$/, companyExtractor: (h) => h.split(".")[0] ?? null },
  // BambooHR: {company}.bamboohr.com
  { hostMatcher: /\.bamboohr\.com$/, companyExtractor: (h) => h.split(".")[0] ?? null },
  // Breezy: {company}.breezy.hr
  { hostMatcher: /\.breezy\.hr$/, companyExtractor: (h) => h.split(".")[0] ?? null },
  // Teamtailor: {company}.teamtailor.com
  { hostMatcher: /\.teamtailor\.com$/, companyExtractor: (h) => h.split(".")[0] ?? null },
  // Recruitee: {company}.recruitee.com
  { hostMatcher: /\.recruitee\.com$/, companyExtractor: (h) => h.split(".")[0] ?? null },
  // Pinpoint: {company}.pinpointhq.com
  { hostMatcher: /\.pinpointhq\.com$/, companyExtractor: (h) => h.split(".")[0] ?? null },
  // SmartRecruiters: careers.smartrecruiters.com/{company}
  { hostMatcher: /^(?:careers|jobs)\.smartrecruiters\.com$/, companyExtractor: (_, p) => p.split("/").filter(Boolean)[0] ?? null },
];

const KNOWN_COMPANIES: Record<string, string> = {
  "linkedin.com": "LinkedIn",
  "google.com": "Google",
  "careers.google.com": "Google",
  "amazon.com": "Amazon",
  "amazon.jobs": "Amazon",
  "microsoft.com": "Microsoft",
  "careers.microsoft.com": "Microsoft",
  "apple.com": "Apple",
  "meta.com": "Meta",
  "metacareers.com": "Meta",
  "stripe.com": "Stripe",
  "notion.so": "Notion",
  "linear.app": "Linear",
  "vercel.com": "Vercel",
  "anthropic.com": "Anthropic",
  "openai.com": "OpenAI",
  "figma.com": "Figma",
  "github.com": "GitHub / Microsoft",
  "gitlab.com": "GitLab",
  "datadog.com": "Datadog",
  "airbnb.com": "Airbnb",
  "uber.com": "Uber",
  "shopify.com": "Shopify",
  "spotify.com": "Spotify",
  "twitter.com": "X / Twitter",
  "tesla.com": "Tesla",
  "netflix.com": "Netflix",
  "zoom.us": "Zoom",
  "salesforce.com": "Salesforce",
  "oracle.com": "Oracle",
  "ibm.com": "IBM",
  "indeed.com": "Indeed",
  "glassdoor.com": "Glassdoor",
  "levels.fyi": "Levels.fyi",
  "startup.jobs": "Startup Jobs",
  "angel.co": "AngelList / Wellfound",
  "wellfound.com": "Wellfound",
  "workable.com": "Workable",
  "ziprecruiter.com": "ZipRecruiter",
  "monster.com": "Monster",
  "careerbuilder.com": "CareerBuilder",
};

/**
 * Extract a company name guess from a job URL.
 * Returns null if no pattern matches.
 */
export function guessCompanyFromUrl(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  const host = parsed.hostname.toLowerCase();
  const path = parsed.pathname;

  // 1. Check known companies by exact host match
  if (KNOWN_COMPANIES[host]) return KNOWN_COMPANIES[host];

  // 2. Check known companies by subdomain + domain
  const hostParts = host.split(".");
  for (let i = hostParts.length - 2; i >= 0; i--) {
    const subdomain = hostParts.slice(i).join(".");
    if (KNOWN_COMPANIES[subdomain]) return KNOWN_COMPANIES[subdomain];
  }

  // 3. Check ATS patterns
  for (const pattern of ATS_PATTERNS) {
    if (pattern.hostMatcher.test(host)) {
      const company = pattern.companyExtractor(host, path);
      if (company) return capitalizeCompany(company);
    }
  }

  // 4. Fallback: use the subdomain or domain name
  // careers.example.com -> "Example"
  if (host.startsWith("careers.") || host.startsWith("jobs.")) {
    return capitalizeCompany(host.split(".")[1]);
  }

  // linkedin.com/in/company-slug -> use path segment
  if (host.includes("linkedin.com") || host.includes("linkedin.")) {
    return null; // LinkedIn won't tell us the company from URL alone
  }

  // General fallback: first subdomain
  if (hostParts.length >= 3) {
    return capitalizeCompany(hostParts[0]);
  }

  // Second-level domain (example.com -> "Example")
  if (hostParts.length === 2 && !["com", "org", "net", "io", "co", "app"].includes(hostParts[0])) {
    return capitalizeCompany(hostParts[0]);
  }

  return null;
}

/**
 * Try to extract a role hint from the URL path.
 * Many job boards put the role slug in the path.
 */
export function guessRoleFromUrl(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  const path = parsed.pathname;
  const segments = path.split("/").filter(Boolean);

  // Common patterns: /jobs/{id}/{role-slug} or /careers/{role-slug}
  // Look for descriptive segments after numeric IDs
  for (let i = 0; i < segments.length; i++) {
    if (/^\d+$/.test(segments[i]) && i + 1 < segments.length) {
      return segments[i + 1].replace(/-/g, " ");
    }
  }

  // Take the last path segment if it looks like a role (contains hyphens, not numbers only)
  const last = segments[segments.length - 1];
  if (last && last.includes("-") && !/^\d+$/.test(last)) {
    return last.replace(/-/g, " ");
  }

  return null;
}

function capitalizeCompany(name: string): string {
  return name
    .split(/[-_]/)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Validate that a string looks like a real URL.
 */
export function isValidJobUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}